import { useNavigate, useSearch } from "@tanstack/react-router";
import { SignedIn } from "@clerk/clerk-react";
import {
  CheckoutProvider,
  useCheckout,
  PaymentElementProvider,
  PaymentElement,
  usePaymentElement,
  usePlans,
} from "@clerk/clerk-react/experimental";
import { useState, useEffect } from "react";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/card";
import { Spinner } from "@/ui/spinner";
import {
  IconArrowLeft,
  IconCheck,
  IconSparkles,
  IconLock,
  IconAlertCircle,
} from "@tabler/icons-react";
import { cn } from "@/lib/utils";

const POPULAR_PLAN_ID = "orgpro";

// Stripe PaymentElement appearance - matches our dark zinc/rose theme (compact)
const stripeAppearance = {
  colorPrimary: "#f43f5e",
  colorBackground: "#27272a",
  colorText: "#fafafa",
  colorTextSecondary: "#a1a1aa",
  colorSuccess: "#10b981",
  colorDanger: "#ef4444",
  colorWarning: "#f59e0b",
  fontWeightNormal: "400",
  fontWeightMedium: "500",
  fontWeightBold: "600",
  fontSizeXl: "1rem",
  fontSizeLg: "0.875rem",
  fontSizeSm: "0.8125rem",
  fontSizeXs: "0.75rem",
  borderRadius: "0.5rem",
  spacingUnit: "3px",
};

const paymentStyles = `
  .cl-rootBox, .cl-cardBox, .cl-card {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }
  .cl-formFieldInput {
    background: rgb(39 39 42 / 0.5) !important;
    border-color: rgb(63 63 70 / 0.5) !important;
  }
  .cl-formFieldInput:focus {
    border-color: #f43f5e !important;
  }
`;

interface CheckoutContentProps {
  planId: string;
}

function CheckoutContent({ planId }: CheckoutContentProps) {
  const navigate = useNavigate();
  const { checkout } = useCheckout();
  const { data: plans } = usePlans({ for: "organization" });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get the plan details from usePlans
  const plan = plans?.find((p) => p.id === planId);
  const isPopular = plan?.id === POPULAR_PLAN_ID;

  // Start checkout session on mount
  useEffect(() => {
    if (checkout.status === "needs_initialization") {
      checkout.start().catch((err: Error) => {
        setError(err.message || "Failed to initialize checkout");
      });
    }
  }, [checkout]);

  if (checkout.status === "needs_initialization" || !plan) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4 text-center">
          <Spinner className="size-6 text-primary" />
          <p className="text-sm text-zinc-500">Loading checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4 py-12 bg-grid-pattern">
      <style>{paymentStyles}</style>
      <div className="fixed inset-0 bg-gradient-radial pointer-events-none -z-10" />

      <div className="relative w-full max-w-4xl">
        {/* Back Button */}
        <button
          onClick={() => navigate({ to: "/onboarding/pricing" })}
          className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300 transition-colors mb-6"
        >
          <IconArrowLeft className="w-4 h-4" />
          Back to plans
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2.5 mb-3">
            <img
              src="/loomii-pink-white.png"
              alt="Loomii"
              className="w-9 h-9"
            />
          </div>
          <h1 className="text-xl font-semibold text-zinc-100 mb-1.5">
            Complete your subscription
          </h1>
          <p className="text-sm text-zinc-400">
            5-day free trial included · Cancel anytime
          </p>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
            <IconAlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-400">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-300 text-xs"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Order Summary Card */}
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardHeader className="pb-3 pt-4 px-5">
              <div className="flex items-center justify-between">
                <CardTitle className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">
                  Order Summary
                </CardTitle>
                {isPopular && (
                  <Badge className="bg-primary/20 text-primary border-0 text-[10px] px-2 py-0.5">
                    <IconSparkles className="w-3 h-3 mr-1" />
                    Popular
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3 px-5 pb-5">
              {/* Plan Info */}
              <div>
                <h3 className="text-base font-semibold text-zinc-100">
                  {plan.name}
                </h3>
                {plan.description && (
                  <p className="text-xs text-zinc-500 mt-1">{plan.description}</p>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-zinc-800 pt-3 space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-400">Subtotal</span>
                  <span className="text-zinc-100">
                    {plan.fee?.currencySymbol}
                    {plan.fee?.amountFormatted}/mo
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-400">5-day free trial</span>
                  <span className="text-emerald-500">
                    -{plan.fee?.currencySymbol}
                    {plan.fee?.amountFormatted}
                  </span>
                </div>
                <div className="border-t border-zinc-800 pt-2 flex justify-between font-medium">
                  <span className="text-zinc-100 text-sm">Due today</span>
                  <span className="text-primary text-base">$0.00</span>
                </div>
              </div>

              {/* Features */}
              {plan.features && plan.features.length > 0 && (
                <div className="border-t border-zinc-800 pt-3">
                  <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-2">
                    Includes
                  </p>
                  <ul className="space-y-1.5">
                    {plan.features.map((feature: { id: string; name: string }) => (
                      <li
                        key={feature.id}
                        className="flex items-center gap-2 text-xs text-zinc-300"
                      >
                        <IconCheck className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                        {feature.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Form Card */}
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardHeader className="pb-3 pt-4 px-5">
              <CardTitle className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">
                Payment Details
              </CardTitle>
              <p className="text-xs text-zinc-400 mt-1">
                Enter your card information
              </p>
            </CardHeader>
            <CardContent className="px-5 pb-5">
              <PaymentElementProvider stripeAppearance={stripeAppearance} for="organization">
                <PaymentForm
                  isProcessing={isProcessing}
                  setIsProcessing={setIsProcessing}
                  setError={setError}
                />
              </PaymentElementProvider>
            </CardContent>
          </Card>
        </div>

        {/* Security Note */}
        <p className="text-center text-xs text-zinc-600 mt-5 flex items-center justify-center gap-1.5">
          <IconLock className="w-3 h-3" />
          Your payment is secured by Stripe
        </p>
      </div>
    </div>
  );
}

interface PaymentFormProps {
  isProcessing: boolean;
  setIsProcessing: (v: boolean) => void;
  setError: (v: string | null) => void;
}

function PaymentForm({
  isProcessing,
  setIsProcessing,
  setError,
}: PaymentFormProps) {
  const paymentElement = usePaymentElement();
  const { checkout } = useCheckout();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsProcessing(true);

    try {
      // Validate card with Stripe and get payment token
      const result = await paymentElement.submit();
      if (result.error) {
        setError(String(result.error) || "Card validation failed");
        setIsProcessing(false);
        return;
      }

      // Confirm the checkout with Clerk using the payment token
      await checkout.confirm({
        paymentToken: result.data.paymentToken,
        gateway: result.data.gateway,
      });

      // Finalize and redirect
      checkout.finalize({
        navigate: () => navigate({ to: "/dashboard" }),
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Payment processing failed";
      setError(message);
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />

      <Button
        type="submit"
        disabled={isProcessing || !paymentElement.isFormReady}
        className={cn(
          "w-full h-9 text-sm font-medium bg-primary hover:bg-primary/90 text-white",
          isProcessing && "opacity-70"
        )}
      >
        {isProcessing ? (
          <>
            <Spinner className="w-3.5 h-3.5 mr-2" />
            Processing...
          </>
        ) : (
          <>
            <IconLock className="w-3.5 h-3.5 mr-2" />
            Start Free Trial
          </>
        )}
      </Button>

      {/* Dev mode skip button */}
      {import.meta.env.DEV && (
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate({ to: "/dashboard" })}
          className="w-full h-8 text-xs text-zinc-500 border-zinc-700 hover:bg-zinc-800"
        >
          Skip (Dev Mode)
        </Button>
      )}
    </form>
  );
}

export function OnboardingCheckoutPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/onboarding/checkout" });
  const planId = search.planId;
  const planPeriod = search.planPeriod || "month";

  // Missing required params
  if (!planId) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <IconAlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="text-lg font-semibold text-zinc-100">
            Invalid checkout link
          </h2>
          <p className="text-sm text-zinc-500">
            Please select a plan from the pricing page.
          </p>
          <Button
            onClick={() => navigate({ to: "/onboarding/pricing" })}
            className="mt-4"
          >
            Return to Pricing
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SignedIn>
      <CheckoutProvider
        planId={planId}
        planPeriod={planPeriod as "month" | "annual"}
        for="organization"
      >
        <CheckoutContent planId={planId} />
      </CheckoutProvider>
    </SignedIn>
  );
}
